import React, { Fragment, useEffect, useState, useRef } from "react";

const ProductVariantSelection = ({
  variants,
  errorShow,
  setSelectedVariants,
  onValidityChange,
  initialSelections = null,
}) => {
  const [selections, setSelections] = useState([]);
  const initializedRef = useRef(false);
  const ignoreNextUpdateRef = useRef(false);

  // Validate selections against variant requirements
  const validateSelections = (selectionsToValidate) => {
    if (!variants || !variants.length) return true;

    const isValid = variants.every((variant) => {
      if (variant.is_required !== "yes") return true;

      const variantSelection = selectionsToValidate.find(
        (s) => s.variant_id === variant.id
      );

      if (!variantSelection) return false;

      const totalQuantity = variantSelection.options.reduce(
        (sum, opt) => sum + opt.option_quantity,
        0
      );

      // For required variants, check if total matches upto_limit
      if (variant.upto_limit) {
        return totalQuantity === variant.upto_limit;
      }

      // If no upto_limit, check if at least one option is selected
      return totalQuantity > 0;
    });

    return isValid;
  };

  // One-time initialization or when initialSelections change significantly
  useEffect(() => {
    // Skip if no variants or if we're ignoring the next update
    if (!variants || !variants.length || ignoreNextUpdateRef.current) {
      ignoreNextUpdateRef.current = false;
      return;
    }

    // Check if we have valid initialSelections
    const hasValidInitialSelections =
      initialSelections &&
      Array.isArray(initialSelections) &&
      initialSelections.length > 0;

    let newSelections;

    // Only initialize once or when initialSelections are provided
    if (!initializedRef.current || hasValidInitialSelections) {
      if (hasValidInitialSelections) {
        // Use initial selections if provided
        newSelections = initialSelections;
        setSelections(newSelections);
      } else if (!initializedRef.current) {
        // Otherwise create empty selections, but only on first initialization
        newSelections = variants.map((variant) => ({
          variant_type: variant.variant_type,
          variant_id: variant.id,
          options: variant.options.map((option) => ({
            option_id: option.id,
            option_name: option.name_en,
            option_quantity: 0,
            unit_price: parseFloat(option.unit_price),
          })),
        }));
        setSelections(newSelections);
      }

      initializedRef.current = true;

      // After initialization, validate and notify the parent
      if (newSelections) {
        setSelectedVariants(newSelections);

        // Validate on mount and when initialSelections change
        const isValid = validateSelections(newSelections);
        onValidityChange(isValid);
      }
    }
  }, [variants, initialSelections, setSelectedVariants, onValidityChange]);

  // Only handle user-initiated updates
  const updateQuantity = (variantId, optionId, newQuantity) => {
    // This flag tells the useEffect to ignore the next selections update
    ignoreNextUpdateRef.current = true;

    setSelections((prev) => {
      const newSelections = prev.map((variant) => {
        if (variant.variant_id === variantId) {
          return {
            ...variant,
            options: variant.options.map((option) =>
              option.option_id === optionId
                ? { ...option, option_quantity: Math.max(0, newQuantity) }
                : option
            ),
          };
        }
        return variant;
      });

      // After state update, inform parent about changes
      setTimeout(() => {
        setSelectedVariants(newSelections);

        // Use the common validation function
        const isValid = validateSelections(newSelections);
        onValidityChange(isValid);
      }, 0);

      return newSelections;
    });
  };

  const handleCheckboxChange = (variantId, optionId) => {
    const variant = selections.find((v) => v.variant_id === variantId);
    if (!variant) return;

    const option = variant.options.find((o) => o.option_id === optionId);
    if (!option) return;

    const currentQty = option.option_quantity;
    updateQuantity(variantId, optionId, currentQty === 0 ? 1 : 0);
  };

  const handleQuantityChange = (variantId, optionId, adjustment) => {
    const variant = selections.find((v) => v.variant_id === variantId);
    if (!variant) return;

    const option = variant.options.find((o) => o.option_id === optionId);
    if (!option) return;

    const currentQty = option.option_quantity;
    const originalVariant = variants.find((v) => v.id === variantId);

    // Check upto_limit constraint
    if (adjustment > 0 && originalVariant && originalVariant.upto_limit) {
      const currentTotal = variant.options.reduce(
        (sum, opt) => sum + opt.option_quantity,
        0
      );
      if (currentTotal >= originalVariant.upto_limit) return;
    }

    updateQuantity(variantId, optionId, currentQty + adjustment);
  };

  return (
    <div className="space-y-4">
      {variants.map((variant) => {
        const variantSelection = selections.find(
          (s) => s.variant_id === variant.id
        ) || { options: [] };

        return (
          <div key={variant.id} className="space-y-2">
            {/* Variant Header */}
            <div
              className={`flex justify-between items-center p-2 rounded-lg h-10  ${
                errorShow && variant.is_required === "yes"
                  ? "border border-red-500 bg-red-50"
                  : "bg-[#E7DDD8]"
              }`}
            >
              <div className="flex items-center">
                <h3 className="text-base font-semibold">
                  {variant.name_en}
                  <span className="text-xs text-gray-600 ml-1">
                    {variant.variant_type === "size"
                      ? "(Choose 1)"
                      : variant.mat_gathering_combo === "yes"
                      ? `(${variant.variant_caption_en})`
                      : `(Choose up to ${variant.upto_limit})`}
                  </span>
                </h3>
              </div>

              {variant.is_required === "yes" && (
                <div className="flex items-center">
                  {errorShow && (
                    <img
                      src="/Images/Menu/errorIcon.png"
                      alt="error"
                      className="w-4 h-4 mr-2"
                    />
                  )}
                  <span
                    className={`text-sm font-semibold ${
                      errorShow ? "text-red-600" : "text-[#87553D]"
                    }`}
                  >
                    (Required)
                  </span>
                </div>
              )}
            </div>

            {/* Variant Options */}
            <div className="space-y-2">
              {variant.options.map((option, index) => {
                const currentOption = variantSelection.options?.find(
                  (o) => o.option_id === option.id
                ) || { option_quantity: 0 };

                return (
                  <Fragment key={option.id}>
                    {index > 0 && <hr className="border-t border-gray-200" />}
                    <div className="flex justify-between items-center py-2">
                      {variant.selection_option === "single_option" ? (
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={currentOption.option_quantity > 0}
                            onChange={() =>
                              handleCheckboxChange(variant.id, option.id)
                            }
                            className="form-checkbox h-4 w-4 text-indigo-600 rounded"
                          />
                          <span>{option.name_en}</span>
                        </label>
                      ) : (
                        <>
                          <div>
                            <span>{option.name_en}</span>
                            {variant.display_each_price === "yes" ||
                              (variant.display_each_price === "" && (
                                <span className="text-xs text-gray-600 ml-1">
                                  (Each +KWD {option.unit_price})
                                </span>
                              ))}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                handleQuantityChange(variant.id, option.id, -1)
                              }
                              disabled={currentOption.option_quantity === 0}
                              className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                            >
                              <span
                                className="inline-block"
                                style={{ marginBottom: "2px" }}
                              >
                                -
                              </span>
                            </button>
                            <span>{currentOption.option_quantity}</span>
                            <button
                              onClick={() =>
                                handleQuantityChange(variant.id, option.id, 1)
                              }
                              disabled={
                                variantSelection.options?.reduce(
                                  (sum, opt) => sum + opt.option_quantity,
                                  0
                                ) >= variant.upto_limit
                              }
                              className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                            >
                              <span
                                className="inline-block"
                                style={{ marginLeft: "0.5px" }}
                              >
                                +
                              </span>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProductVariantSelection;
